// serviceApi.js
import { API_BASE_URL } from './config.js'; // Assuming config.js exists for API_BASE_URL
import fetchWrapper from '../components/utility/fetchWrapper.js'

const DEFAULT_SCRAPE_PERIOD = 300;
const DEFAULT_SCRAPE_LENGTH = 600;

const parseServiceApiError = (errorText, status) => {
  const raw = String(errorText || "").trim();
  if (!raw) {
    return status === 409 ? "Metric is already onboarded." : "Request failed.";
  }

  try {
    const parsed = JSON.parse(raw);
    const nestedMessage = parsed?.error?.message;
    const detail = parsed?.detail ?? parsed?.error?.details;

    const combined = [
      typeof nestedMessage === "string" ? nestedMessage : "",
      typeof detail === "string" ? detail : "",
      typeof parsed?.message === "string" ? parsed.message : "",
    ]
      .filter(Boolean)
      .join(" ");

    if (
      status === 409
      || combined.includes("SERVICE_ALREADY_EXISTS")
      || combined.includes("already exists")
    ) {
      return "Metric is already onboarded.";
    }

    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (typeof nestedMessage === "string" && nestedMessage.trim()) {
      return nestedMessage;
    }
    if (typeof parsed?.message === "string" && parsed.message.trim()) {
      return parsed.message;
    }
  } catch (_e) {
    if (raw.includes("SERVICE_ALREADY_EXISTS") || raw.includes("already exists")) {
      return "Metric is already onboarded.";
    }
  }

  return raw;
};

const normalizeAwsAccountId = (awsId) =>
  String(awsId || "").replace(/^aws_/, "").trim();

const ACCOUNT_WIDE_SERVICE_TYPES = new Set([
  "Glue",
  "AWS/Glue",
  "AWS/StepFunctions",
  "AWS/ElasticMapReduce",
  "AWS/MWAA",
  "AmazonMWAA",
]);

const mergeServiceRecords = (allData = []) => {
  const mergedServices = {};

  allData.forEach((service) => {
    const isAccountWide = ACCOUNT_WIDE_SERVICE_TYPES.has(service.type);
    const key = isAccountWide
      ? service.type
      : service.pipeline_id
        ? `${service.type}|${service.pipeline_id}`
        : service.type;
    if (!key) return;

    if (!mergedServices[key]) {
      mergedServices[key] = { ...service };
      return;
    }

    const existing = mergedServices[key];
    const existingMetrics = existing.metrics || [];
    const newMetrics = service.metrics || [];
    const allMetrics = [...existingMetrics, ...newMetrics];
    const uniqueMetrics = Array.from(
      new Map(allMetrics.map((m) => [m.name || m, m])).values(),
    );

    const existingRegions = existing.regions || [];
    const newRegions = service.regions || [];
    const uniqueRegions = [...new Set([...existingRegions, ...newRegions])];

    const existingTags = existing.searchTags || [];
    const newTags = service.searchTags || [];
    const allTags = [...existingTags, ...newTags];
    const uniqueTags = Array.from(
      new Map(allTags.map((t) => [t.key, t])).values(),
    );

    mergedServices[key] = {
      ...existing,
      metrics: uniqueMetrics,
      regions: uniqueRegions,
      searchTags: uniqueTags,
      period: service.period || existing.period,
      length: service.length || existing.length,
    };
  });

  return Object.values(mergedServices);
};

const serviceBelongsToAccount = (service, awsAccountId) => {
  const target = normalizeAwsAccountId(awsAccountId);
  if (!target) return true;

  const roles = service?.roles || [];
  for (const role of roles) {
    const arn =
      typeof role === "object"
        ? role.roleArn || role.role_arn || ""
        : String(role || "");
    if (arn.includes(`:${target}:`)) return true;
  }

  const serviceAccount = normalizeAwsAccountId(service?.aws_account_id || "");
  return serviceAccount === target;
};

const parseServicesResponse = async (response) => {
  if (!response?.ok) return [];
  try {
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.warn("[getServices] Failed to parse response:", e);
    return [];
  }
};

export const serviceApi = {
  getServices: async (awsId) => {
    try {
      const numericId = normalizeAwsAccountId(awsId);
      if (!numericId) return [];

      const legacyFormat = `awsid-${numericId}`;
      const newFormat = `awsid-aws_${numericId}`;

      const [legacyResponse, newResponse] = await Promise.allSettled([
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${legacyFormat}`),
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${newFormat}`),
      ]);

      let allData = [];

      if (legacyResponse.status === "fulfilled") {
        if (legacyResponse.value.ok) {
          allData = allData.concat(await parseServicesResponse(legacyResponse.value));
        } else if (legacyResponse.value.status === 500) {
          const errorData = await legacyResponse.value.json().catch(() => ({}));
          throw new Error(
            errorData.detail ||
              errorData.error?.message ||
              `Server error: ${legacyResponse.value.status}`,
          );
        }
      }

      if (newResponse.status === "fulfilled") {
        if (newResponse.value.ok) {
          allData = allData.concat(await parseServicesResponse(newResponse.value));
        } else if (newResponse.value.status === 500) {
          const errorData = await newResponse.value.json().catch(() => ({}));
          throw new Error(
            errorData.detail ||
              errorData.error?.message ||
              `Server error: ${newResponse.value.status}`,
          );
        }
      }

      let result = mergeServiceRecords(allData);

      if (result.length === 0) {
        const allServicesResponse = await fetchWrapper(`${API_BASE_URL}/api/v1/services/`);
        const allServices = await parseServicesResponse(allServicesResponse);
        const filtered = allServices.filter((service) =>
          serviceBelongsToAccount(service, numericId),
        );
        result = mergeServiceRecords(filtered);
        if (result.length > 0) {
          console.warn(
            `[getServices] Account-scoped endpoints returned no rows; recovered ${result.length} service(s) from global list for account ${numericId}`,
          );
        }
      }

      console.log(
        `[getServices] Fetched ${allData.length} account-scoped row(s), merged to ${result.length} unique service(s) for account ${numericId}`,
      );
      return result;
      
    } catch (error) {
      console.error("Error fetchWrappering services:", error);
      throw error;
    }
  },

  // New method to fetchWrapper namespaces based on account and region
  getNamespaces: async (awsId, region = "us-east-1") => {
    try {
      console.log(`fetchWrappering namespaces for ${awsId} in region ${region}`);
      
      // Fetch from both legacy and new formats
      const legacyFormat = `awsid-${awsId}`;
      const newFormat = `awsid-aws_${awsId}`;
      
      const [legacyResponse, newResponse] = await Promise.allSettled([
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${legacyFormat}/namespaces?region=${region}`),
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${newFormat}/namespaces?region=${region}`)
      ]);
      
      let allNamespaces = [];
      
      // Process legacy format response
      if (legacyResponse.status === 'fulfilled' && legacyResponse.value.ok) {
        try {
          const data = await legacyResponse.value.json();
          if (Array.isArray(data)) {
            allNamespaces = allNamespaces.concat(data);
          }
        } catch (e) {
          console.warn('Failed to parse legacy namespaces response:', e);
        }
      }
      
      // Process new format response
      if (newResponse.status === 'fulfilled' && newResponse.value.ok) {
        try {
          const data = await newResponse.value.json();
          if (Array.isArray(data)) {
            allNamespaces = allNamespaces.concat(data);
          }
        } catch (e) {
          console.warn('Failed to parse new namespaces response:', e);
        }
      }
      
      // Deduplicate namespaces by namespace field and merge regions
      const namespaceMap = new Map();
      for (const ns of allNamespaces) {
        if (!ns?.namespace) continue;
        const existing = namespaceMap.get(ns.namespace);
        if (existing) {
          const mergedRegions = [
            ...new Set([...(existing.regions || []), ...(ns.regions || [])]),
          ];
          namespaceMap.set(ns.namespace, { ...existing, regions: mergedRegions });
        } else {
          namespaceMap.set(ns.namespace, { ...ns, regions: ns.regions || [] });
        }
      }
      const uniqueNamespaces = Array.from(namespaceMap.values());
      
      console.log("Namespaces response (merged):", uniqueNamespaces);
      return uniqueNamespaces;
    } catch (error) {
      console.error("Error fetchWrappering namespaces:", error);
      throw error;
    }
  },

  // Corrected method to fetchWrapper metrics for a namespace
  getMetricsForNamespace: async (awsId, region, namespace) => {
    try {
      console.log(`fetchWrappering metrics for ${namespace} in ${region} for account ${awsId}`);
      const encodedNamespace = encodeURIComponent(namespace);
      
      // Fetch from both legacy and new formats
      const legacyFormat = `awsid-${awsId}`;
      const newFormat = `awsid-aws_${awsId}`;
      
      const [legacyResponse, newResponse] = await Promise.allSettled([
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${legacyFormat}/metrics?namespace=${encodedNamespace}&region=${region}`),
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${newFormat}/metrics?namespace=${encodedNamespace}&region=${region}`)
      ]);
      
      let allMetrics = [];
      
      // Process legacy format response
      if (legacyResponse.status === 'fulfilled' && legacyResponse.value.ok) {
        try {
          const data = await legacyResponse.value.json();
          if (data && data.metrics && Array.isArray(data.metrics)) {
            allMetrics = allMetrics.concat(data.metrics);
          } else if (Array.isArray(data)) {
            allMetrics = allMetrics.concat(data);
          }
        } catch (e) {
          console.warn('Failed to parse legacy metrics response:', e);
        }
      }
      
      // Process new format response
      if (newResponse.status === 'fulfilled' && newResponse.value.ok) {
        try {
          const data = await newResponse.value.json();
          if (data && data.metrics && Array.isArray(data.metrics)) {
            allMetrics = allMetrics.concat(data.metrics);
          } else if (Array.isArray(data)) {
            allMetrics = allMetrics.concat(data);
          }
        } catch (e) {
          console.warn('Failed to parse new metrics response:', e);
        }
      }
      
      // Deduplicate metrics by metric name
      const uniqueMetrics = Array.from(
        new Map(allMetrics.map(m => [
          typeof m === 'string' ? m : (m.name || m.metricName || JSON.stringify(m)),
          m
        ])).values()
      );
      
      console.log("Metrics response (merged):", uniqueMetrics);
      return uniqueMetrics;
    } catch (error) {
      console.error("Error fetchWrappering metrics for namespace:", error);
      throw error;
    }
  },

  // Get full metrics response including recommended metrics
  getMetricsWithRecommendations: async (awsId, region, namespace) => {
    try {
      console.log(`fetchWrappering full metrics with recommendations for ${namespace} in ${region} for account ${awsId}`);
      const encodedNamespace = encodeURIComponent(namespace);
      
      // Fetch from both legacy and new formats
      const legacyFormat = `awsid-${awsId}`;
      const newFormat = `awsid-aws_${awsId}`;
      
      const [legacyResponse, newResponse] = await Promise.allSettled([
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${legacyFormat}/metrics?namespace=${encodedNamespace}&region=${region}`),
        fetchWrapper(`${API_BASE_URL}/api/v1/services/${newFormat}/metrics?namespace=${encodedNamespace}&region=${region}`)
      ]);
      
      let allMetrics = [];
      let allRecommendedMetrics = [];
      
      // Process legacy format response
      if (legacyResponse.status === 'fulfilled' && legacyResponse.value.ok) {
        try {
          const data = await legacyResponse.value.json();
          if (data) {
            if (Array.isArray(data.metrics)) {
              allMetrics = allMetrics.concat(data.metrics);
            }
            if (Array.isArray(data.recommended_metrics)) {
              allRecommendedMetrics = allRecommendedMetrics.concat(data.recommended_metrics);
            }
          }
        } catch (e) {
          console.warn('Failed to parse legacy recommendations response:', e);
        }
      }
      
      // Process new format response
      if (newResponse.status === 'fulfilled' && newResponse.value.ok) {
        try {
          const data = await newResponse.value.json();
          if (data) {
            if (Array.isArray(data.metrics)) {
              allMetrics = allMetrics.concat(data.metrics);
            }
            if (Array.isArray(data.recommended_metrics)) {
              allRecommendedMetrics = allRecommendedMetrics.concat(data.recommended_metrics);
            }
          }
        } catch (e) {
          console.warn('Failed to parse new recommendations response:', e);
        }
      }
      
      // Deduplicate metrics and recommended metrics
      const uniqueMetrics = Array.from(
        new Map(allMetrics.map(m => [
          typeof m === 'string' ? m : (m.name || m.metricName || JSON.stringify(m)),
          m
        ])).values()
      );
      
      const uniqueRecommendedMetrics = Array.from(
        new Map(allRecommendedMetrics.map(m => [
          typeof m === 'string' ? m : (m.name || m.metricName || JSON.stringify(m)),
          m
        ])).values()
      );
      
      const result = {
        metrics: uniqueMetrics,
        recommended_metrics: uniqueRecommendedMetrics
      };
      
      console.log("Full metrics response with recommendations (merged):", result);
      return result;
    } catch (error) {
      console.error("Error fetchWrappering metrics with recommendations:", error);
      throw error;
    }
  },

  getServicesType: async () => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/services/getservices`);
      if (!response.ok) throw new Error("Failed to fetchWrapper service types");
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetchWrappering service types:", error);
      throw error;
    }
  },

  createService: async (serviceData) => {
    try {
      console.log("Service Data:", serviceData);

      const payload = {
        type: serviceData.type,
        regions: serviceData.regions,
        period: parseInt(serviceData.period) || DEFAULT_SCRAPE_PERIOD,
        length: parseInt(serviceData.length) || DEFAULT_SCRAPE_LENGTH,
        metrics: serviceData.metrics,
        roles: serviceData.roles, // Use roles, which is an array of ARNs
        searchTags: serviceData.searchTags
      };

      console.log("Payload:", payload);

      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/services/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(parseServiceApiError(errorData, response.status));
      }
      return await response.json();
    } catch (error) {
      console.error("Error creating service:", error);
      throw error;
    }
  },

  deleteService: async (serviceType, awsAccountId = null) => {
    try {
      const doubleEncodedServiceType = encodeURIComponent(encodeURIComponent(serviceType));
      const accountQuery = awsAccountId
        ? `?aws_account_id=${encodeURIComponent(String(awsAccountId).replace(/^aws_/, ""))}`
        : "";
      const url = `${API_BASE_URL}/api/v1/services/${doubleEncodedServiceType}${accountQuery}`;

      console.log("DELETE URL:", url);

      const response = await fetchWrapper(url, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to delete service: ${errorData}`);
      }
      return true;
    } catch (error) {
      console.error("Error deleting service:", error);
      throw error;
    }
  },

  updateService: async (serviceName, serviceData) => {
    try {
      console.log("Service Data:", serviceData);

      const payload = {
        regions: serviceData.regions,
        period: parseInt(serviceData.period) || DEFAULT_SCRAPE_PERIOD,
        length: parseInt(serviceData.length) || DEFAULT_SCRAPE_LENGTH,
        metrics: serviceData.metrics,
        searchTags: serviceData.searchTags || []
      };

      console.log("Payload", payload);

      const doubleEncodedServiceName = encodeURIComponent(encodeURIComponent(serviceName));
      const url = `${API_BASE_URL}/api/v1/services/${doubleEncodedServiceName}`;

      console.log("PATCH URL:", url);
      console.log("Payload:", JSON.stringify(payload, null, 2));

      const response = await fetchWrapper(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to update service: ${errorData}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error updating service:", error);
      throw error;
    }
  },

  // Add this function to the serviceApi object
  getActiveCosting: async (serviceData) => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/services/active-costing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(serviceData),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to calculate service cost: ${errorData}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error calculating service cost:", error);
      throw error;
    }
  },

  getMetricmetadataCosting: async ({
  account_id,
  timerange,
  service,
  region = 'us-east-1',
  include_metrics = true,
  include_namespace_breakdown = false,
  limit = 1000,
  offset = 0,
   }) => {
    try {
      const params = new URLSearchParams({
        account_id,
        timerange,
        region,
        include_metrics: String(include_metrics),
        include_namespace_breakdown: String(include_namespace_breakdown),
      });
      if (service) params.append("service", service);
      if (limit != null) params.append("limit", String(limit));
      if (offset != null) params.append("offset", String(offset));

      const response = await fetchWrapper(`${API_BASE_URL}/metrics-metadata?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {       
        const errorData = await response.text();
        throw new Error(`Failed to calculate service cost: ${errorData}`);
      }
      console.log("Metric Metadata Costing response:", response);
      return await response.json();
    } catch (error) {
      console.error("Error calculating service cost:", error);
      throw error;
    }
  },

  // Get metrics namespaces from Prometheus-based endpoint
  getMetricsNamespaces: async (account_id, region = 'us-east-1', timerange = '24h') => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/metrics-namespaces?account_id=${account_id}&region=${region}&timerange=${timerange}`
      );
      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Error response: ${errorData}`);
        throw new Error(`Failed to fetch metrics namespaces: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      console.log("Metrics namespaces response:", data);
      return data;
    } catch (error) {
      console.error("Error fetching metrics namespaces:", error);
      throw error;
    }
  },

  // Get available regions from Prometheus metric labels for a given account.
  getMetricsRegions: async (account_id, timerange = '24h') => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/metrics-regions?account_id=${account_id}&timerange=${timerange}`
      );
      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Error response: ${errorData}`);
        throw new Error(`Failed to fetch metrics regions: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching metrics regions:", error);
      throw error;
    }
  },

  // Get billed metrics pipeline cost from Cost Explorer (CloudWatch + AMP buckets).
  getMetricsBilledCost: async (
    account_id,
    region = 'us-east-1',
    timerange = '30d',
    cost_metric = 'UnblendedCost'
  ) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/metrics-billed-cost?account_id=${account_id}&region=${region}&timerange=${timerange}&cost_metric=${cost_metric}`
      );
      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Error response: ${errorData}`);
        throw new Error(`Failed to fetch metrics billed cost: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching metrics billed cost:", error);
      throw error;
    }
  },
};
